import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export type FilterMode = 'client' | 'server';

export interface ClientFilters {
  status: string[];
  createdByName: string[];
  currentlyAssignedToName: string[];
}

export interface ServerFilters {
  searchTerm?: string;
  status?: string;
  workflowType?: string;
  currentlyAssigned?: boolean;
  sortDescending?: boolean;
  sortColumn?: string;
}

export interface UseUnifiedFiltersReturn {
  filterMode: FilterMode;
  setFilterMode: (mode: FilterMode) => void;
  clientFilters: ClientFilters;
  serverFilters: ServerFilters;
  updateClientFilter: (
    field: keyof ClientFilters,
    value: string | string[]
  ) => void;
  updateServerFilter: (
    key: keyof ServerFilters,
    value: string | boolean | undefined
  ) => void;
  updateSort: (column: string, order: 'asc' | 'desc') => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  getApiParams: () => Record<string, string | boolean | number>;
}

export const useUnifiedFilters = (): UseUnifiedFiltersReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterMode, setFilterModeState] = useState<FilterMode>('client');

  const setFilterMode = useCallback(
    (mode: FilterMode) => {
      setFilterModeState(mode);

      // Clear URL parameters for the inactive mode when switching
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        if (mode === 'client') {
          // Clear server filter parameters
          [
            'searchTerm',
            'status',
            'workflowType',
            'currentlyAssigned',
            'sortDescending',
            'sortColumn',
          ].forEach((key) => {
            newParams.delete(key);
          });
        } else {
          // Clear client filter parameters
          [
            'client_status',
            'client_createdByName',
            'client_currentlyAssignedToName',
            'client_type',
          ].forEach((key) => {
            newParams.delete(key);
          });
        }

        return newParams;
      });
    },
    [setSearchParams]
  );

  // Parse client-side filters from URL
  const clientFilters = useMemo(() => {
    const filters: ClientFilters = {
      status: [],
      createdByName: [],
      currentlyAssignedToName: [],
    };

    // Parse filter parameters from URL
    searchParams.forEach((value, key) => {
      if (key.startsWith('client_')) {
        const field = key.replace('client_', '') as keyof ClientFilters;
        if (Object.prototype.hasOwnProperty.call(filters, field)) {
          // Support both single values and pipe-separated multi-values
          const values = value.includes('|') ? value.split('|') : [value];
          filters[field] = values;
        }
      }
    });

    return filters;
  }, [searchParams]);

  // Parse server-side filters from URL
  const serverFilters = useMemo(() => {
    const filters: ServerFilters = {};

    const searchTerm = searchParams.get('searchTerm');
    const status = searchParams.get('status');
    const workflowType = searchParams.get('workflowType');
    const currentlyAssigned = searchParams.get('currentlyAssigned');
    const sortDescending = searchParams.get('sortDescending');

    if (searchTerm) filters.searchTerm = searchTerm;
    if (status) filters.status = status;
    if (workflowType) filters.workflowType = workflowType;
    if (currentlyAssigned === 'true') filters.currentlyAssigned = true;
    if (sortDescending === 'true') filters.sortDescending = true;
    if (sortDescending === 'false') filters.sortDescending = false;

    return filters;
  }, [searchParams]);

  const updateClientFilter = useCallback(
    (field: keyof ClientFilters, value: string | string[]) => {
      // Only update URL if we're in client mode
      if (filterMode !== 'client') return;

      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        const values = Array.isArray(value)
          ? value
          : value
            ? [value]
            : [];

        if (values.length === 0) {
          newParams.delete(`client_${field}`);
        } else {
          newParams.set(`client_${field}`, values.join('|'));
        }

        return newParams;
      });
    },
    [setSearchParams, filterMode]
  );

  const updateServerFilter = useCallback(
    (key: keyof ServerFilters, value: string | boolean | undefined) => {
      // Only update URL if we're in server mode
      if (filterMode !== 'server') return;

      console.log('🔍 updateServerFilter called:', { key, value });
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        if (key === 'sortDescending') {
          // Special handling for sortDescending
          if (value === true) {
            newParams.set(key, 'true');
          } else if (value === false) {
            newParams.set(key, 'false');
          } else {
            // Remove sortDescending when undefined
            newParams.delete(key);
          }
        } else if (value === undefined || value === '' || value === false) {
          // Remove filter if value is empty/false/undefined
          newParams.delete(key);
        } else {
          // Set filter value
          newParams.set(key, String(value));
        }

        console.log('🔍 New URL params:', newParams.toString());
        return newParams;
      });
    },
    [setSearchParams, filterMode]
  );

  const updateSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      // Only update URL if we're in server mode
      if (filterMode !== 'server') return;

      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        if (column) {
          newParams.set('sortColumn', column);
          // Only set sortDescending to true, remove it when false (default behavior)
          if (order === 'desc') {
            newParams.set('sortDescending', 'true');
          } else {
            newParams.delete('sortDescending');
          }
        } else {
          newParams.delete('sortColumn');
          newParams.delete('sortDescending');
        }

        return newParams;
      });
    },
    [setSearchParams, filterMode]
  );

  const clearFilters = useCallback(() => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      // Only clear filters for the current mode
      if (filterMode === 'client') {
        [
          'client_status',
          'client_createdByName',
          'client_currentlyAssignedToName',
          'client_type',
        ].forEach((key) => {
          newParams.delete(key);
        });
      } else {
        [
          'searchTerm',
          'status',
          'workflowType',
          'currentlyAssigned',
          'sortDescending',
          'sortColumn',
        ].forEach((key) => {
          newParams.delete(key);
        });
      }

      return newParams;
    });
  }, [setSearchParams, filterMode]);

  const hasActiveFilters = useMemo(() => {
    if (filterMode === 'client') {
      return Object.values(clientFilters).some(
        (filterValues) => filterValues.length > 0
      );
    } else {
      return Object.values(serverFilters).some((value) => value !== undefined);
    }
  }, [filterMode, clientFilters, serverFilters]);

  const getApiParams = useCallback(() => {
    const params: Record<string, string | boolean | number> = {};

    if (serverFilters.searchTerm) params.searchTerm = serverFilters.searchTerm;
    if (serverFilters.status) params.status = serverFilters.status;
    if (serverFilters.workflowType)
      params.workflowType = serverFilters.workflowType;
    if (serverFilters.currentlyAssigned !== undefined)
      params.currentlyAssigned = serverFilters.currentlyAssigned;
    // Include sortDescending if it's explicitly set
    if (serverFilters.sortDescending !== undefined)
      params.sortDescending = serverFilters.sortDescending;
    if (serverFilters.sortColumn) params.sortColumn = serverFilters.sortColumn;

    return params;
  }, [serverFilters]);

  return {
    filterMode,
    setFilterMode,
    clientFilters,
    serverFilters,
    updateClientFilter,
    updateServerFilter,
    updateSort,
    clearFilters,
    hasActiveFilters,
    getApiParams,
  };
};
